import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { Card } from 'components/Card';
import { Alert } from 'components/alert/Alert';
import { Button } from 'components/button/Button';
import { DataField } from 'components/DataField';
import { Dropzone } from 'components/Dropzone';
import { ConfirmDialog } from 'components/dialog/ConfirmDialog';
import { Pill, PillColor } from 'components/pill/Pill';
import { Table } from 'components/table/Table';
import { formatIban } from '@utils/iban';
import { getDisplayError } from '@utils/get-display-error';
import { applyExactAccountsImport, previewExactAccountsImport } from '../../../../supplier/models';
import type {
  ExactImportResult,
  ExactImportRow,
  ExactImportRowStatus,
} from '../../../../supplier/endpoints/ExactAccountsImportEndpoint.types';

const STATUS: Record<ExactImportRowStatus, { label: string; color: PillColor }> = {
  update: { label: 'Bijwerken', color: 'positive' },
  unchanged: { label: 'Ongewijzigd', color: 'muted' },
  notFound: { label: 'Niet gevonden', color: 'neutral' },
  noVatNumber: { label: 'Geen btw-nr.', color: 'neutral' },
  noBankAccount: { label: 'Geen rekening', color: 'warning' },
  ambiguous: { label: 'Dubbelzinnig', color: 'warning' },
  conflict: { label: 'Conflict', color: 'negative' },
  invalid: { label: 'Ongeldig', color: 'negative' },
};

const HEADERS = [
  { id: 'status', name: 'Status' },
  { id: 'exact', name: 'Exact' },
  { id: 'supplier', name: 'Vervoerder' },
  { id: 'accountingId', name: 'ID Boekhouding' },
  { id: 'iban', name: 'IBAN' },
  { id: 'messages', name: 'Opmerkingen' },
];

function change(oldValue: string | null, newValue: string | null): string {
  if (!newValue) {
    return oldValue || '-';
  }
  return `${oldValue || '-'} → ${newValue}`;
}

/**
 * One-time backfill of IBAN/BIC and Exact relatiecodes on existing carriers, matched by
 * VAT number. Always previewed first; apply re-reads the same file.
 */
export const ExactAccountsImportSection: React.FC = () => {
  const [file, setFile] = useState<globalThis.File | null>(null);
  const [result, setResult] = useState<ExactImportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const preview = async () => {
    if (!file) {
      return;
    }
    setIsLoading(true);
    try {
      setResult(await previewExactAccountsImport(file));
    } catch (err) {
      captureException(err);
      toast.error(`Kon bestand niet lezen: ${getDisplayError(err)}`);
    }
    setIsLoading(false);
  };

  const apply = async () => {
    if (!file) {
      return;
    }
    const applied = await applyExactAccountsImport(file);
    setResult(applied);
    toast.success(`${applied.totals.toUpdate} vervoerders bijgewerkt`);
  };

  const totals = result?.totals;

  return (
    <Card
      title="Import uit Exact"
      subtitle="Vult IBAN, BIC en ID Boekhouding aan op bestaande vervoerders, gekoppeld op btw-nummer. Maakt geen vervoerders aan."
    >
      <div className="grid gap-4">
        <Dropzone
          files={file ? [file] : []}
          onUpload={(files) => {
            setFile(files[0] ?? null);
            setResult(null);
          }}
          accept=".xml,text/xml,application/xml"
        />

        <div className="flex gap-2">
          <Button onTrigger={preview} isDisabled={!file || isLoading} isLoading={isLoading}>
            Voorbeeld
          </Button>
          {result && !result.applied && result.totals.toUpdate > 0 && (
            <ConfirmDialog
              triggerText={`${result.totals.toUpdate} vervoerders bijwerken`}
              title="Import toepassen?"
              description="Bankgegevens en ID Boekhouding worden overschreven met de waarden uit Exact, zonder tweede goedkeuring. Elke wijziging wordt gelogd."
              submitText="Toepassen"
              onSubmit={apply}
            />
          )}
        </div>

        {result?.applied && <Alert type="success">Import toegepast.</Alert>}

        {totals && (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <DataField title="Relaties in bestand">{`${totals.accountsRead} (${totals.supplierAccounts} leveranciers)`}</DataField>
            <DataField title="Gekoppeld">{totals.matched}</DataField>
            <DataField title="Bij te werken">
              {`${totals.toUpdate} (IBAN ${totals.ibanUpdates}, ID ${totals.accountingIdUpdates})`}
            </DataField>
            <DataField title="Ongewijzigd">{totals.unchanged}</DataField>
            <DataField title="Niet gevonden / geen btw-nr.">{`${totals.notFound} / ${totals.noVatNumber}`}</DataField>
            <DataField title="Geen rekening in Exact">{totals.noBankAccount}</DataField>
            <DataField title="Manueel na te kijken">
              {`${totals.ambiguous + totals.conflicts + totals.invalid} (dubbelzinnig ${totals.ambiguous}, conflict ${totals.conflicts}, ongeldig ${totals.invalid})`}
            </DataField>
            <DataField title="Self billing zonder geldige IBAN">
              <Link to="/internal/suppliers?missingIban=true" className="link-text">
                {totals.activeSuppliersWithoutIban}
              </Link>
            </DataField>
          </div>
        )}

        {result && (
          <Table<ExactImportRow>
            idKey="exactCode"
            headers={HEADERS}
            data={result.rows}
            isRowClickable={false}
            mapData={(row) => [
              <Pill color={STATUS[row.status].color} size="sm">
                {STATUS[row.status].label}
              </Pill>,
              `${row.exactCode} · ${row.exactName}`,
              row.supplierId ? (
                <Link to={`/internal/suppliers/${row.supplierId}/general`} className="link-text">
                  {row.supplierName}
                </Link>
              ) : (
                row.vatNumber
              ),
              change(row.oldAccountingId, row.newAccountingId),
              change(formatIban(row.oldIban) || null, formatIban(row.newIban) || null),
              row.messages.join(' '),
            ]}
          />
        )}
      </div>
    </Card>
  );
};
